// Package imports:
import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// import './HomeTradesLatest.scss';
// Component imports:
import StickyHeaderTable from '../common/StickyHeaderTable';
import SortFilterTable from '../common/SortFilterTable';
// Action imports:
import { requestPublicTradesForGroup } from '../../redux-store/actions/publicActions';
// Service imports:
import { formatNumber, formatDate, turnDateToSimpleTime, getUniqueTickIdForPublicTradeForGroup } from '../../services/utils';
import { timestampObjectCompareFunction } from '../../services/sortUtils';
import { useRequestedDataByDate } from '../../services/hooks';
// Type imports:
import { RootState } from '../../redux-store/reducers';

type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps;

const HomeTradesLatest: React.FC<Props> = ({
    publicTradesForGroupByTradingDay,
    currentTradingDay,
    accessTokenV2,
    groupId,
    requestPublicTradesForGroup
}) => {
    // Fetch data.
    useEffect(() => {
        const publicTradesForGroup = publicTradesForGroupByTradingDay?.[currentTradingDay];
        if (publicTradesForGroup === undefined && groupId !== null) {
            requestPublicTradesForGroup(currentTradingDay, accessTokenV2, groupId);
        }
    }, [ publicTradesForGroupByTradingDay, currentTradingDay, accessTokenV2, groupId, requestPublicTradesForGroup ]);

    const [ trades, isLoading, isError ] = useRequestedDataByDate(publicTradesForGroupByTradingDay, currentTradingDay);
    const sortedTrades = [...trades].sort(timestampObjectCompareFunction);

    // ========================== Render Functions ==========================
    // -------------- Render Symbol --------------
    const RenderSymbolBody = ({ trade }: { trade: any }) => {
        return (
            <a href={`/sigti/${trade.symbol}?tradingDay=${turnDateToSimpleTime(new Date(currentTradingDay))}#${getUniqueTickIdForPublicTradeForGroup(trade)}`}>
                {trade.symbol}
            </a>
        )
    }

    return (
        <div id='HomeTradesLatest'>
            <h5 className='SectionTitle'>Our Trades</h5>
            <div className='TableWrapper'>
                <StickyHeaderTable
                    hideScrollbar={false}
                >
                    <SortFilterTable
                        data={sortedTrades}
                        isLoading={isLoading}
                        isError={isError}
                        columns={[{
                            title: 'Symbol',
                            dataAccessor: (trade) => <RenderSymbolBody trade={trade} />,
                            textAlign: 'left',
                            simpleSortable: ({symbol}) => symbol,
                            sortTiebreaker: timestampObjectCompareFunction
                        }, {
                            title: 'Timestamp',
                            textAlign: 'left',
                            dataAccessor: ({timestamp}) => formatDate(timestamp, true),
                            sortable: timestampObjectCompareFunction
                        }, {
                            title: 'Trade Class',
                            dataAccessor: ({trade_class}) => trade_class,
                            textAlign: 'left',
                            simpleSortable: ({trade_class}) => trade_class,
                            sortTiebreaker: timestampObjectCompareFunction
                        }, {
                            title: 'Volume',
                            dataAccessor: ({volume}) => formatNumber(volume, 'volume'),
                            simpleSortable: ({volume}) => volume,
                            sortTiebreaker: timestampObjectCompareFunction
                        }, {
                            title: 'Price',
                            dataAccessor: ({price}) => formatNumber(price),
                            simpleSortable: ({price}) => price,
                            sortTiebreaker: timestampObjectCompareFunction
                        }]}
                    />
                </StickyHeaderTable>
            </div>
        </div>
    );
}

function mapStateToProps({ publicReducer, dashboardReducer, loginReducer }: RootState) {
    const { publicTradesForGroupByTradingDay } = publicReducer;
    const { currentTradingDay } = dashboardReducer;
    const { accessTokenV2, groupId } = loginReducer;
    return {
        publicTradesForGroupByTradingDay,
        currentTradingDay,
        accessTokenV2,
        groupId
    }
}

const connector = connect(mapStateToProps, { requestPublicTradesForGroup });
export default connector(HomeTradesLatest);